import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { MdInfo, MdOutlineArrowBack } from 'react-icons/md';
import GaugeComponent from 'react-gauge-component';
import moment from 'moment';
import PrepSharpLogo from 'assets/svg/logo.svg';
import axios from 'config/axios';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { setError } from 'store/error';
import ButtonLink from 'components/ButtonLink';
import LevelBar from 'components/LevelBar';
import ScoreWithScale from 'components/ScoreWithScale2';
import useWindowSize from 'hooks/useWindowSize';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import { MULTIPLE_CHOICES } from 'global/constants';

import './index.css';

const timePerQuestionRW = 64 * 60 / 54;
const timePerQuestionMath = 70 * 60 / 44;

function DigitalSATReport() {
    const [testData, setTestData] = useState(null);
    const [sectionIndex, setSectionIndex] = useState(0);
    const [moduleIndex, setModuleIndex] = useState(0);
    const [modeInfo, setModeInfo] = useState(false);

    const windowSize = useWindowSize();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { testId } = useParams();
    // const modeInfoRef = useOutsideClickHandler(() => setTimeout(() => setModeInfo(false), 10));

    const formatTime = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = (hours && duration.minutes() < 10) ? `0${duration.minutes()}` : duration.minutes();
        const seconds = duration.seconds() < 10 ? `0${duration.seconds()}` : duration.seconds();

        return hours ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
    }

    const formatTime2 = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = duration.minutes();
        const seconds = duration.seconds();

        return hours 
            ? `${hours} hr ${minutes} min ${seconds} sec`
            : minutes
            ? `${minutes} min ${seconds} sec`
            : `${seconds} sec`;
    }

    useEffect(() => {
        axios.get(`tests/digital/result/sat/${testId}/`)
            .then(res => {
                // console.log(res.data);
                setTestData(res.data);
            })
            .catch(error => {
                // console.log(error);
                dispatch(setError(error));
            });
    }, []);

    if (!testData) return <></>;

    return (
        <div className='layout'>
            <Helmet>
                <title>
                    {testData.name} Report | PrepSharp Students
                </title>
            </Helmet>
            <div className='digitalReport__bar'>
                <button
                    className='digitalReport__back'
                    // onClick={() => testData.testType === 'Digital SAT'
                    //     ? navigate('/tests/digital-sat')
                    //     : testData.testType === 'Digital PSAT'
                    //     ? navigate('/tests/digital-psat')
                    //     : navigate('/tests')
                    // }
                    onClick={() => navigate('/tests/digital-sat')}
                >
                    <MdOutlineArrowBack className='digitalReport__backIcon' />
                    Back to My Tests
                </button>
                {/* <h2 className='digitalReport__studentName'>
                    {testData.studentName}
                </h2> */}
            </div>
            <div className='digitalReport__headerGroup'>
                <div className='digitalReport__headerSection'>
                    <div className='digitalReport__headerInfo'>
                        <p>
                            Name: {testData.studentName}
                        </p>
                        <p>
                            Test name: {testData.name}
                        </p>
                        <p>
                            Tested on: {moment(testData.testDate).format('MMMM D YYYY')}
                        </p>
                    </div>
                </div>
                <div className='digitalReport__headerSection'>
                    <img
                        className='digitalReport__headerLogo'
                        alt='logo'
                        src={PrepSharpLogo}
                    />
                </div>
                {modeInfo &&
                    <div className='digitalReport__modePopup'>
                        {/* <IoCloseCircle className='digitalReport__modeClose' onClick={() => setModeInfo(false)} /> */}
                        <p>
                            <i>Review Mode</i> enables you to review your completed practice test (for test section).
                            <br/><br/>
                            Test questions answered correctly are marked in <span className='digitalReport__modePopupGreen'>green</span> while incorrect responses are marked in <span className='digitalReport__modePopupRed'>red</span> for each item. Use the <i>Show Answer</i> button on any test question to reveal the credited response.
                            <br/><br/>
                            In addition, the Question Menu in the footer expands to provide a quick, color-coded overview of your performance.
                        </p>
                    </div>
                }
            </div>
            <div className='digitalReport__headerGroup'>
                <h1 className='digitalReport__headerTitle'>
                    Test Result Overview
                </h1>
                <div className='digitalReport__buttonGroup'>
                    {/* <Button className='digitalReport__button' secondary onClick={handlePrint}>
                        PRINT REPORT
                    </Button> */}
                    <ButtonLink className='digitalReport__button' to={`/review/digital-sat/${testId}`}>
                        REVIEW MODE
                        <MdInfo
                            className='digitalReport__modeInfo'
                            // onClick={() => setModeInfo(!modeInfo)}
                            onMouseEnter={() => setModeInfo(true)}
                            onMouseLeave={() => setModeInfo(false)}
                        />
                    </ButtonLink>
                </div>
            </div>
            <div className='digitalReport__card'>
                <div className='digitalReport__cardRow'>
                    <div className='digitalReport__scoresGroup'>
                        <h2 className='digitalReport__scoreTitle'>TOTAL SCORE</h2>
                        <ScoreWithScale
                            className='digitalReport__scoreWithScale'
                            score={Math.floor(testData.score)}
                            scoreMin={Math.floor(testData.minScore)}
                            scoreMax={Math.floor(testData.maxScore)}
                            fontSize={70}
                            // fontSize={windowSize.width > 576
                            //     ? 100
                            //     : 70
                            // }
                        />
                        <div className='digitalReport__scoreDivider'></div>
                        <h2 className='digitalReport__scoreTitle'>SECTION SCORES</h2>
                        {testData.testSections.map((testSection, t) => (<>
                            <h3 className='digitalReport__sectionScoreTitle'>
                                {testSection.subject}
                            </h3>
                            <ScoreWithScale
                                className='digitalReport__scoreWithScale'
                                score={Math.floor(testSection.score)}
                                scoreMin={Math.floor(testSection.minScore)}
                                scoreMax={Math.floor(testSection.maxScore)}
                            />
                            {t < testData.testSections.length - 1 &&
                                <div className='digitalReport__scoreDivider'></div>
                            }
                        </>))}
                    </div>
                    <div className='digitalReport__overview'>
                        <h2 className='digitalReport__cardSubTitle'>
                            Raw Scores
                        </h2>
                        <div className='digitalReport__overviewRow'>
                            {testData.testSections.map(testSection => (
                                <div className='digitalReport__overviewItem'>
                                    <h4 className='digitalReport__overviewLabel'>
                                        {testSection.subject}
                                    </h4>
                                    <table className='digitalReport__overviewTable'>
                                        <tbody>
                                            <tr>
                                                <th></th>
                                                <th>Correct</th>
                                                <th>Time</th>
                                            </tr>
                                            {testData.testModules.filter(testModule => (
                                                testModule.subject === testSection.subject
                                            )).map((testModule, t) => (
                                                <tr>
                                                    <td>
                                                        Module {testModule.moduleNumber} {testModule.moduleNumber === 2 && (testModule.hard ? '(Hard)' : '(Easy)')}
                                                    </td>
                                                    <td>{testModule.answers.filter(answer => answer.isCorrect).length}/{testModule.answers.length}</td>
                                                    <td>{formatTime(testModule.answers.reduce((total, answer) => (
                                                        total + answer.timeSpent
                                                    ), 0))}</td>
                                                </tr>
                                            ))}
                                            <tr>
                                                <td>Total</td>
                                                <td>{testData.testModules.filter(testModule => (
                                                    testModule.subject === testSection.subject
                                                )).reduce((total, testModule) => (
                                                    total + testModule.answers.filter(answer => answer.isCorrect).length
                                                ), 0)}/{testData.testModules.filter(testModule => (
                                                    testModule.subject === testSection.subject
                                                )).reduce((total, testModule) => (
                                                    total + testModule.answers.length
                                                ), 0)}</td>
                                                <td>{formatTime(testData.testModules.filter(testModule => (
                                                    testModule.subject === testSection.subject
                                                )).reduce((total, testModule) => (
                                                    total + testModule.answers.reduce((time, answer) => (
                                                        time + answer.timeSpent
                                                    ), 0)
                                                ), 0))}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            ))}
                        </div>
                        <h2 className='digitalReport__cardSubTitle'>
                            Category Scores
                        </h2>
                        <div className='digitalReport__overviewRow'>
                            {testData.testSections.map((testSection, s) => (
                                <div className='digitalReport__overviewItem'>
                                    <h4 className='digitalReport__overviewLabel'>
                                        {testSection.subject}
                                    </h4>
                                    {testSection.rootCategories.map(rootCategory => (<>
                                        <h5 className='digitalReport__levelLabel'>
                                            {rootCategory.name}
                                        </h5>
                                        <LevelBar
                                            className={s % 2 === 0 ? 'digitalReport__levelGreen' : 'digitalReport__levelOrange'}
                                            percent={100*rootCategory.numCorrect/rootCategory.numQuestions}
                                            label={`${rootCategory.numCorrect}/${rootCategory.numQuestions}`}
                                        />
                                    </>))}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <h2 className='digitalReport__cardSubTitle--centered'>
                    Section Time Averages
                </h2>
                <div className='digitalReport__overviewRow'>
                    {testData.testSections.map(testSection => (
                        <div className='digitalReport__overviewItem'>
                            <h4 className='digitalReport__overviewLabel--centered'>
                                {testSection.subject}
                            </h4>
                            <table className='digitalReport__timeTable'>
                                <tbody>
                                    <tr>
                                        <th></th>
                                        <th>Section Time</th>
                                        <th>Time Per Question</th>
                                    </tr>
                                    {testSection.rootCategories.map(rootCategory => (
                                        <tr>
                                            <td>
                                                {rootCategory.name}
                                            </td>
                                            <td>
                                                {formatTime2(rootCategory.timeSpent)}
                                            </td>
                                            <td>
                                                <GaugeComponent
                                                    className='digitalReport__timeGauge'
                                                    type='semicircle'
                                                    value={rootCategory.timeSpent/rootCategory.numQuestions}
                                                    minValue={0}
                                                    maxValue={testSection.subject === 'Reading and Writing'
                                                        ?   timePerQuestionRW * 3
                                                        :   testSection.subject === 'Math'
                                                        ?   timePerQuestionMath * 3
                                                        :   180
                                                    }
                                                    arc={{
                                                        'subArcs': [
                                                            { length: 0.33, color: "#5BE12C"},
                                                            { length: 0.33, color: "#F5CD19"},
                                                            { length: 0.33, color: "#EA4228"}
                                                        ]
                                                    }}
                                                    // style={{ 'width': '200px' }}
                                                    pointer={{
                                                        'width': 10,
                                                    }}
                                                    labels={{
                                                        'valueLabel': {
                                                            // 'formatTextValue': (value) => formatTime(value),
                                                            // 'style': {
                                                            //     'fontSize': '40px',
                                                            //     'fill': '#000',
                                                            //     'textShadow': null,
                                                            //     'marginTop': 20
                                                            // }
                                                            'hide': true
                                                        },
                                                        'tickLabels': {
                                                            'hideMinMax': true
                                                        }
                                                    }}
                                                />
                                                <p>{formatTime2(rootCategory.timeSpent/rootCategory.numQuestions)}</p>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            </div>
            {/* <div className='digitalReport__header'>
                <div className='digitalReport__headerRow'>
                    <h1 className='digitalReport__title'>
                        {testData.name}
                    </h1>
                    <p>
                        {moment(testData.testSections[testData.testSections.length - 1].testDate).format('MMMM D YYYY')}
                    </p>
                </div>
            </div>
            <h2 className='digitalReport__subHeader'>
                Test result overview
            </h2>
            <div className='digitalReport__scores'>
                {testData.score && <>
                    <div className='digitalReport__scoreColumn'>
                        <h3 className='digitalReport__scoreMainTitle'>
                            Your Total Score
                        </h3>
                        <ScoreWithScale
                            score={Math.floor(testData.score)}
                            scoreMin={Math.floor(testData.minScore)}
                            scoreMax={Math.floor(testData.maxScore)}
                            fontSize={windowSize.width > 576
                                ? 100
                                : 70
                            }
                        />
                    </div>
                    <div className='digitalReport__scoreGroup'>
                        {testData.testSections.map(testSection => (
                            <div className='digitalReport__scoreColumn' key={testData.name + testSection.subject}>
                                <h3 className='digitalReport__scoreSubTitle'>
                                    Your {testSection.subject} Score
                                </h3>
                                <ScoreWithScale
                                    score={Math.floor(testSection.score)}
                                    scoreMin={Math.floor(testSection.minScore)}
                                    scoreMax={Math.floor(testSection.maxScore)}
                                />
                            </div>
                        ))}
                    </div>
                    <div className='digitalReport__buttonGroup'>
                        <ButtonLink
                            className='digitalReport__button'
                            to={`/review/digital-sat/${testId}`}
                        >
                            REVIEW QUESTIONS
                        </ButtonLink>
                    </div>
                </>}
            </div>
            {testData.testSections.map((testSection, t) => {
                const weakCategories = testSection.categories.filter(category => (
                    category.numCorrect < category.numQuestions
                )).sort((a, b) => (a.numCorrect/a.numQuestions === b.numCorrect/b.numQuestions
                    ? b.numQuestions - a.numQuestions
                    : a.numCorrect/a.numQuestions - b.numCorrect/b.numQuestions
                ));

                const topWeakCategories = weakCategories.length > 5 ? weakCategories.slice(0, 5) : weakCategories;

                const maxNumQuestions = Math.max(...topWeakCategories.map(category => category.numQuestions));
                
                if (topWeakCategories.length > 0) {
                    return (
                        <div className='digitalReport__weakAreas' key={'weak' + testSection.subject}>
                            <h3 className='digitalReport__weakAreasTitle'>
                                Your Focus Areas in {testSection.subject}
                            </h3>
                            <table className='digitalReport__weakAreasTable'>
                                <tbody>
                                    <tr>
                                        <th>{testSection.subject}</th>
                                        <th></th>
                                        <th></th>
                                    </tr>
                                    {topWeakCategories.map(category => (
                                        <tr key={category.name}>
                                            <td>{category.name}</td>
                                            <td>
                                                <div className='digitalReport__weakAreasGraph'>
                                                    <div style={{
                                                        'width': `${Math.max(1, 100*category.numCorrect/maxNumQuestions)}%`
                                                    }}></div>
                                                    <div style={{
                                                        'width': `${100*category.numQuestions/maxNumQuestions}%`
                                                    }}></div>
                                                </div>
                                            </td>
                                            <td>{category.numCorrect}/{category.numQuestions} Correct</td>
                                        </tr>
                                    ))}
                                    <tr>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    );
                }
                else return <></>;
            })} */}
            {/* <h2 className='digitalReport__subHeader'>
                Test result detailed breakdown
            </h2> */}
            <div className='digitalReport__questions'>
                <h2 className='digitalReport__questionsHeader'>
                    Questions Overview
                </h2>
                <div className='digitalReport__sectionTabs'>
                    {testData.testSections.map((testSection, t) => (
                        <button
                            className={sectionIndex === t
                                ? 'digitalReport__sectionTab--active'
                                : 'digitalReport__sectionTab'
                            }
                            onClick={() => {
                                setSectionIndex(t);
                                setModuleIndex(0);
                            }}
                            key={testSection.subject}
                        >
                            {testSection.subject}
                        </button>
                    ))}
                    <div className={sectionIndex === 0
                        ? 'digitalReport__sectionTabIndicatorLeft'
                        : 'digitalReport__sectionTabIndicatorRight'
                    }></div>
                </div>
                {testData.testModules.filter(testModule => (
                    testModule.subject === testData.testSections[sectionIndex].subject
                )).length > 1 &&
                    <div className='digitalReport__moduleTabs'>
                        {testData.testModules.filter(testModule => (
                            testModule.subject === testData.testSections[sectionIndex].subject
                        )).map((testModule, t) => (
                            <button
                                className={moduleIndex === t
                                    ? 'digitalReport__moduleTab--active'
                                    : 'digitalReport__moduleTab'
                                }
                                onClick={() => setModuleIndex(t)}
                                key={testModule.name}
                            >
                                Module {testModule.moduleNumber} {testModule.moduleNumber === 2 && (testModule.hard ? ' (Hard)' : ' (Easy)')}
                            </button>
                        ))}
                        <div className={moduleIndex === 0
                            ? 'digitalReport__moduleTabIndicatorLeft'
                            : 'digitalReport__moduleTabIndicatorRight'
                        }></div>
                    </div>
                }
                <div className='digitalReport__questionsOverview'>
                    <div className='digitalReport__questionsSummary'>
                        <div className='digitalReport__questionsSummaryItem'>
                            <p className='digitalReport__questionSummaryNumber'>
                                {testData.testModules.filter(testModule => (
                                    testModule.subject === testData.testSections[sectionIndex].subject
                                    && testModule.moduleNumber === moduleIndex + 1
                                )).reduce((total, testModule) => (
                                    total + testModule.answers.length
                                ), 0)}
                            </p>
                            <p className='digitalReport__questionSummaryText'>
                                Total Questions
                            </p>
                        </div>
                        <div className='digitalReport__questionsSummaryItem'>
                            <p className='digitalReport__questionSummaryNumber'>
                                {testData.testModules.filter(testModule => (
                                    testModule.subject === testData.testSections[sectionIndex].subject
                                    && testModule.moduleNumber === moduleIndex + 1
                                )).reduce((total, testModule) => (
                                    total + testModule.answers.filter(answer => answer.isCorrect).length
                                ), 0)}
                            </p>
                            <p className='digitalReport__questionSummaryText'>
                                Correct Answers
                            </p>
                        </div>
                        <div className='digitalReport__questionsSummaryItem'>
                            <p className='digitalReport__questionSummaryNumber'>
                                {testData.testModules.filter(testModule => (
                                    testModule.subject === testData.testSections[sectionIndex].subject
                                    && testModule.moduleNumber === moduleIndex + 1
                                )).reduce((total, testModule) => (
                                    total + testModule.answers.filter(answer => !answer.isCorrect).length
                                ), 0)}
                            </p>
                            <p className='digitalReport__questionSummaryText'>
                                Incorrect Answers
                            </p>
                        </div>
                    </div>
                    {/* {testData.testModules.filter(testModule => (
                        testModule.subject === testData.testSections[sectionIndex].subject
                    )).map(testModule => <>
                        <p className='digitalReport__moduleNumber'>Module {testModule.moduleNumber}</p>
                        <table className='digitalReport__questionsTable'>
                            <tbody>
                                <tr>
                                    <th>Question</th>
                                    <th>Correct Answer</th>
                                    <th>Your Answer</th>
                                </tr>
                                    {testModule.answers.map(answer => (
                                        <tr key={testModule.name + answer.questionNumber}>
                                            <td>{answer.questionNumber}</td>
                                            <td>{answer.correctChoice !== null
                                                ?   MULTIPLE_CHOICES[answer.correctChoice]
                                                :   answer.correctAnswers.toString().replaceAll(',', ', ')
                                            }</td>
                                            <td>{answer.answer !== null
                                                ?   (answer.correctAnswers.includes(answer.answer)
                                                        ? <p>{answer.answer}; Correct</p>
                                                        : <p className='digitalReport__questionIncorrect'>{answer.answer}; Incorrect</p>
                                                    )
                                                :   answer.choice !== null
                                                ?   (answer.choice === answer.correctChoice
                                                        ? <p>{MULTIPLE_CHOICES[answer.choice]}; Correct</p>
                                                        : <p className='digitalReport__questionIncorrect'>{MULTIPLE_CHOICES[answer.choice]}; Incorrect</p>
                                                    )
                                                :   <p className='digitalReport__questionOmitted'>Omitted</p>
                                            }</td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </>)} */}
                    <table className='digitalReport__questionsTable'>
                        <tbody>
                            <tr>
                                <th>Question</th>
                                <th>Correct Answer</th>
                                <th>Your Answer</th>
                                <th>Time Spent</th>
                            </tr>
                            {testData.testModules.filter(testModule => (
                                testModule.subject === testData.testSections[sectionIndex].subject
                            ))[moduleIndex].answers.map(answer => (
                                <tr key={testData.testModules[moduleIndex].name + answer.questionNumber}>
                                    <td>{answer.questionNumber}</td>
                                    <td>{answer.correctChoice !== null
                                        ?   MULTIPLE_CHOICES[answer.correctChoice]
                                        :   answer.correctAnswers.toString().replaceAll(',', ', ')
                                    }</td>
                                    <td>{answer.answer !== null
                                        ?   (answer.correctAnswers.includes(answer.answer)
                                                ? <p>{answer.answer}; Correct</p>
                                                : <p className='digitalReport__questionIncorrect'>{answer.answer}; Incorrect</p>
                                            )
                                        :   answer.choice !== null
                                        ?   (answer.choice === answer.correctChoice
                                                ? <p>{MULTIPLE_CHOICES[answer.choice]}; Correct</p>
                                                : <p className='digitalReport__questionIncorrect'>{MULTIPLE_CHOICES[answer.choice]}; Incorrect</p>
                                            )
                                        :   <p className='digitalReport__questionOmitted'>Omitted</p>
                                    }</td>
                                    <td>
                                        <p className={answer.timeSpent > (testData.testModules[moduleIndex].subject === 'Reading and Writing'
                                                    ? timePerQuestionRW * 1.5
                                                    : testData.testModules[moduleIndex].subject === 'Math'
                                                    ? timePerQuestionMath * 1.5
                                                    : 60 * 1.5
                                                )
                                            ?   'digitalReport__timeSpentBad'
                                            :   answer.timeSpent > (testData.testModules[moduleIndex].subject === 'Reading and Writing'
                                                    ? timePerQuestionRW
                                                    : testData.testModules[moduleIndex].subject === 'Math'
                                                    ? timePerQuestionMath
                                                    : 60
                                                )
                                            ?   'digitalReport__timeSpentWarning'
                                            :   'digitalReport__timeSpentGood'
                                        }>
                                            {formatTime(answer.timeSpent)}
                                        </p>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {/* <table className='digitalReport__questionsTable'>
                    <tbody>
                        <tr>
                            <th>Module</th>
                            <th>Question</th>
                            <th>Correct Answer</th>
                            <th>Your Answer</th>
                        </tr>
                        {testData.testModules.map(testModule => (
                            testModule.answers.map(answer => (
                                <tr key={testModule.name + answer.questionNumber}>
                                    <td>Module {testModule.moduleNumber}</td>
                                    <td>{answer.questionNumber}</td>
                                    <td>{answer.correctChoice !== null
                                        ?   MULTIPLE_CHOICES[answer.correctChoice]
                                        :   answer.correctAnswers.toString().replaceAll(',', ', ')
                                    }</td>
                                    <td>{answer.answer !== null
                                        ?   (answer.correctAnswers.includes(answer.answer)
                                                ? <p>{answer.answer}; Correct</p>
                                                : <p className='digitalReport__questionIncorrect'>{answer.answer}; Incorrect</p>
                                            )
                                        :   answer.choice !== null
                                        ?   (answer.choice === answer.correctChoice
                                                ? <p>{MULTIPLE_CHOICES[answer.choice]}; Correct</p>
                                                : <p className='digitalReport__questionIncorrect'>{MULTIPLE_CHOICES[answer.choice]}; Incorrect</p>
                                            )
                                        :   <p className='digitalReport__questionOmitted'>Omitted</p>
                                    }</td>
                                </tr>
                            ))
                        ))}
                    </tbody>
                </table> */}
            </div>
        </div>
    );
}

export default DigitalSATReport;